import {
  API_CLIENT_ID,
  API_CLIENT_SECRET,
  API_ENDPOINTS,
  getAppURL,
} from "../config";
import { API_URL_KEY, http } from "../utils/http";
import { Base64 } from "../utils/utils";
import { store } from "../State/store/configureStore";
import {
  isFacilityAdmin,
  isMpsAdmin,
  isMPSSupportStaff,
  isPatientOrMember,
  isStaff,
} from "../security/roles";
import staffService from "./staffService";
import { doE164 } from "../utils/phone";
import StorageService from "./storageService";
import Messages from "../Shared/Messages";
import {
  SET_FACILITY_ID,
  UPDATE_PREFERRED_LOCALE,
  UPDATE_SPACE_INFO,
} from "../State/actions/actionTypes";
import I18nProvider from "../../src/services/I18nProvider";
import StatusesService from "./statusesService";
import FeaturesService from "./featuresService";
import globalCacheService from "./globalCacheService";
import patientViewTabsStorageService from "./patientViewTabsStorageService";
import { devLog } from "../utils/devLog";
import tokenRefreshService from "./tokenRefreshService";

export const PATIENT_VIEW_TABS = "PATIENT_VIEW_TABS";
export const ONBOARDING_LOCALE = "ONBOARDING_LOCALE";
export const PREFERRED_LOCALE = "preferredLocale";

function handleAuthSuccess(data) {
  if (data.access_token) {
    http.setToken(data.access_token);

    // Schedule token refresh if we have refresh_token and expires_in
    if (data.refresh_token && data.expires_in) {
      tokenRefreshService.scheduleRefresh(data.expires_in);
    }

    return {
      token: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: data.expires_in,
      tokenTimestamp: Date.now(),
    };
  } else {
    throw new Error("No access token granted");
  }
}

function makeURLEncodedAuthRequest(body) {
  return http.request(API_ENDPOINTS.get_token, "post", body, {
    Authorization:
      "Basic " + Base64.encode(API_CLIENT_ID + ":" + API_CLIENT_SECRET),
    "Content-type": "application/x-www-form-urlencoded; charset=utf-8",
  });
}

function convertToFormData(data) {
  return Object.entries(data)
    .map(([key, val]) => `${key}=${val}`)
    .join("&");
}

function makeLoginRequest(data) {
  data = convertToFormData(data);
  return new Promise((resolve, reject) => {
    makeURLEncodedAuthRequest(data)
      .then((response) => {
        if (!response.error) {
          return resolve(handleAuthSuccess(response));
        }
        if (response.error === "mfa_required") {
          resolve({
            nextStep: "MFA",
            token: response.mfa_token,
            mechanism: response.mfa_mechanism,
          });
        } else {
          throw new Error(response.error);
        }
      })
      .catch((error) => {
        reject(error.message ? error.message.toString() : null);
      });
  });
}

class UserService {
  logIn(username, password) {
    const data = {
      grant_type: "password",
      client_id: API_CLIENT_ID,
      username: encodeURIComponent(username),
      password: encodeURIComponent(password),
    };
    return makeLoginRequest(data);
  }

  logInWithMfa(code, mfaToken, mfaMechanism) {
    const data = {
      grant_type: "mfa",
      mfa_code: code,
      mfa_token: mfaToken,
      mfa_mechanism: mfaMechanism,
    };
    return makeLoginRequest(data);
  }

  resendOtp(mfaToken) {
    return http.request(API_ENDPOINTS.resendOtp(), "post", { mfaToken });
  }

  updateToken(token, refreshToken, expiresIn, tokenTimestamp) {
    devLog("UPDATE TOKEN");
    http.setToken(token);
    this.getSpaceInfo(true);

    // Initialize token refresh if we have refresh token info
    if (refreshToken) {
      tokenRefreshService.initializeFromStorage(
        refreshToken,
        expiresIn,
        tokenTimestamp,
      );
    }
  }

  removeToken() {
    http.removeToken();
    tokenRefreshService.stop();
  }

  /**
   * Get information about the current space with caching
   * @param {boolean} updateStore - Whether to update the Redux store with this data
   * @returns {Promise<Object>} Space information
   */
  async getSpaceInfo(updateStore) {
    // Use caching to prevent duplicate API calls for space info
    const cacheKey = "user_space_info";

    try {
      // If updateStore is true, bypass cache to ensure we get fresh data
      let response;
      if (updateStore) {
        // Make a fresh API call and clear the cache
        globalCacheService.clearCache(cacheKey);
        response = await http.request(API_ENDPOINTS.getSpaceInfo());
      } else {
        // Use cached data if available, otherwise make API call
        response = await globalCacheService.cacheApiCall(cacheKey, () =>
          http.request(API_ENDPOINTS.getSpaceInfo()),
        );
      }

      // Ensure preferred locale is set
      if (!response) {
        devLog("No space info found, returning default values");
        return null;
      }
      response.preferredLocale =
        response.preferredLocale || response.defaultLocale || "en_US";

      // Update store if requested
      if (updateStore) {
        store.dispatch({ type: UPDATE_SPACE_INFO, data: response });

        // Set facility ID if user is a facility admin
        if (isFacilityAdmin(response.role)) {
          const facilityId = response.facilityId;
          store.dispatch({ type: SET_FACILITY_ID, facilityId });
        }
      }

      return response;
    } catch (error) {
      console.error("Error fetching space info:", error);
      throw error;
    }
  }

  acceptInvite(
    inviteCode,
    email,
    phone,
    challengeFields,
    preValidationRequired,
  ) {
    let data = { inviteCode };
    if (email && email !== "") {
      data.email = email;
    } else if (phone && phone !== "") {
      data.phoneNumber = phone;
    }
    if (challengeFields && challengeFields.length > 0) {
      data.challengeFields = challengeFields;
    }
    data.acceptedTermsAndCondition = true;
    if (preValidationRequired) {
      data.acceptedPreValidationTC = true;
    }

    data.locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);

    return http.request(API_ENDPOINTS.acceptInvite(), "post", data);
  }

  fetchSpaceDetailsFromInviteCode(inviteCode, email, phone) {
    let data = { inviteCode };
    if (email && email !== "") {
      data.email = email;
    } else if (phone && phone !== "") {
      data.phoneNumber = phone;
    }
    return http.request(
      API_ENDPOINTS.fetchSpaceDetailsFromInviteCode(),
      "post",
      data,
    );
  }

  acceptTerms(inviteCode, email, phone) {
    let data = { inviteCode };
    if (email && email !== "") {
      data.email = email;
    } else if (phone && phone !== "") {
      data.phoneNumber = phone;
    }
    return http.request(API_ENDPOINTS.acceptTerms(), "post", data);
  }

  confirmCode(confirmationCode, inviteCode, email, phone) {
    let data = { smsCode: confirmationCode, inviteCode };
    if (email && email !== "") {
      data.email = email;
    } else if (phone && phone !== "") {
      data.phoneNumber = phone;
    }
    data.acceptedTermsAndCondition = true;

    return http.request(
      API_ENDPOINTS.confirmationCodeWithTerms(),
      "post",
      data,
    );
  }

  setCredentials(loginUserId, password, inviteCode, email, phone) {
    let data = { loginUserId, password, inviteCode };
    if (email && email !== "") {
      data.email = email;
    } else if (phone && phone !== "") {
      data.phoneNumber = phone;
    }
    data.locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    return http.request(API_ENDPOINTS.setCredentials(), "post", data);
  }

  processForgotPasswordRequest(data) {
    return http.request(API_ENDPOINTS.forgotPassword, "post", data);
  }

  resetPassword(data) {
    return http.request(API_ENDPOINTS.resetPassword, "post", data);
  }

  getAllSpaces() {
    return http.request(API_ENDPOINTS.allSpaces).then((data) => {
      if (data && data._embedded && data._embedded.spaceDetails) {
        return data._embedded.spaceDetails || [];
      } else {
        return [];
      }
    });
  }

  changeSpace(newSpaceId) {
    return http.request(API_ENDPOINTS.changeSpace(newSpaceId), "post");
  }

  changePassword(password, newPassword) {
    return http.request(API_ENDPOINTS.changePassword, "post", {
      password,
      newPassword,
    });
  }

  /**
   * Update staff availability status
   * @param {string} availabilityStatus - New availability status
   * @returns {Promise<Object>} Updated profile data
   */
  updateAvailabilityStatus = async (availabilityStatus) => {
    try {
      const response = await http.request(
        API_ENDPOINTS.loadStaffProfile,
        "patch",
        {
          availabilityStatus,
        },
      );

      // Update the profile cache since availability status is part of the profile
      this.clearProfileCache();

      return response;
    } catch (error) {
      console.error("Error updating availability status:", error);
      throw error;
    }
  };

  /**
   * Load user profile based on role, with caching
   * @returns {Promise<Object>} User profile data
   */
  async loadProfile() {
    // Use in-memory cache to prevent duplicate calls
    if (this._profileCache) {
      return this._profileCache;
    }

    const role = store.getState().user.role;
    try {
      let profileData;

      if (isStaff(role)) {
        profileData = await staffService.loadProfile();
      } else if (isFacilityAdmin(role)) {
        profileData = await http.request(
          API_ENDPOINTS.loadFacilityAdminProfile,
        );
      } else if (isMpsAdmin(role)) {
        profileData = await http.request(API_ENDPOINTS.loadMpsAdminProfile);
      } else {
        throw new Error("Unknown user role for profile loading");
      }

      // Cache the profile data
      this._profileCache = profileData;

      return profileData;
    } catch (error) {
      console.error("Error loading user profile:", error);
      throw error;
    }
  }

  /**
   * Clear the profile cache (call this when profile might have changed)
   */
  clearProfileCache() {
    this._profileCache = null;
  }

  /**
   * Edit user profile based on role
   * @param {Object} data - Updated profile data
   * @returns {Promise<Object>} Updated profile data
   */
  async editProfile(data) {
    const role = store.getState().user.role;
    try {
      let updatedProfile;

      if (isStaff(role)) {
        updatedProfile = await staffService.editProfile(data);
      } else if (isFacilityAdmin(role)) {
        updatedProfile = await http.request(
          API_ENDPOINTS.loadFacilityAdminProfile,
          "patch",
          data,
        );
      } else if (isMpsAdmin(role)) {
        updatedProfile = await http.request(
          API_ENDPOINTS.loadMpsAdminProfile,
          "patch",
          data,
        );
      } else {
        throw new Error("Unknown user role for profile editing");
      }

      // Update the cache with the new profile data
      this._profileCache = updatedProfile;

      return updatedProfile;
    } catch (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }
  }

  sendSmsEMailCampaigns(data) {
    return http.request(API_ENDPOINTS.sendToPatient, "post", data);
  }

  sendStatusMessageCampaigns(data, patientIds, getPatientsResource, notifyUsers, addContentToInteraction) {
    let campaignData = {
      campaignMode: "PATIENT_SEARCH_RESOURCE",
      statusMessage: { ...data, senderCategory: "PATIENT" },
      patientIds: [].concat(...patientIds),
      getPatientsResource: getPatientsResource,
      notifyUsers: notifyUsers !== false,
    };

    if (addContentToInteraction) {
      campaignData.addContentToInteraction = true;
    }

    return http.request(API_ENDPOINTS.campaigns, "post", campaignData);
  }

  sendPatientDocumentsCampaigns(data, patientIds, getPatientsResource) {
    let campaignData = {
      campaignMode: "PATIENT_SEARCH_RESOURCE",
      patientDocument: data,
      patientIds: [].concat(...patientIds),
      getPatientsResource: getPatientsResource,
    };

    return http.request(API_ENDPOINTS.campaigns, "post", campaignData);
  }

  assignCarePlanCampaign(careplanId, patientIds, getPatientsResource) {
    let campaignData = {
      campaignMode: "PATIENT_SEARCH_RESOURCE",
      newCareplanId: careplanId,
      patientIds: [].concat(...patientIds),
      getPatientsResource: getPatientsResource,
    };
    return http.request(API_ENDPOINTS.campaigns, "post", campaignData);
  }

  /**
   * Get bulk action requests status for progress tracking
   * @param {string} jobId - Optional job ID to get specific job status
   * @returns {Promise} - Bulk action request(s) with progress info
   */
  getBulkActionRequests(jobId) {
    let url = API_ENDPOINTS.bulkActionRequests;
    if (jobId) {
      url += `?job-id=${encodeURIComponent(jobId)}`;
    }
    return http.request(url, "get");
  }

  /**
   * Cancel a bulk action job. Removes all pending (NEW) actions from the queue.
   * @param {string} jobId - The job ID to cancel
   * @returns {Promise} - CancelResult with cancelled count
   */
  cancelBulkActionRequest(jobId) {
    return http.request(
      `${API_ENDPOINTS.bulkActionRequests}/${encodeURIComponent(jobId)}`,
      "delete",
    );
  }

  fetchChallengeFields(code) {
    let locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    let api = API_ENDPOINTS.challengeFields(code);
    if (locale) {
      api += "?locale=" + locale;
    }
    return http.request(api);
  }

  fetchChallengeFieldsFromFacilityCode(code) {
    let locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    let api = API_ENDPOINTS.challengeFieldsFromFacilityCode(code);
    if (locale) {
      api += "?locale=" + locale;
    }
    return http.request(api);
  }

  fetchAddPatientProfileFromCode(code) {
    return http.request(
      API_ENDPOINTS.fetchAddPatientProfileFromCode(code),
      "get",
      null,
      null,
      null,
      null,
      null,
      true,
    );
  }

  fetchOnboardingPatientDataTemplate(patientId, type) {
    let locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    let api = API_ENDPOINTS.onboardingPatientDataTemplate(patientId, type);
    if (locale) {
      api += "&locale=" + locale;
    }
    return http.request(api);
  }

  onboardingConfirmIdentity(
    inviteCode,
    challengeFields,
    acceptedTermsAndCondition,
    onboardingType,
    isVerification,
    verificationData,
  ) {
    let data = { acceptedTermsAndCondition };

    if (onboardingType === "signup") {
      data.facilityCode = inviteCode;
    } else {
      data.inviteCode = inviteCode;
    }

    if (challengeFields && challengeFields.length > 0) {
      let clonedChallengeFields = JSON.parse(JSON.stringify(challengeFields));
      for (let i = 0; i < clonedChallengeFields.length; i++) {
        let attribute = clonedChallengeFields[i];
        let av = attribute.value;
        if (attribute.type === "STRING") {
          if (av && av.valueString) {
            av.valueString = av.valueString.trim();
          }
        }

        if (attribute.type === "OBSERVATION") {
          if (
            av &&
            av.valueObservation &&
            av.valueObservation.value &&
            av.valueObservation.value.valueString
          ) {
            av.valueObservation.value.valueString =
              av.valueObservation.value.valueString.trim();
          }
        }
        if (
          attribute.value &&
          (attribute.dataField === "PatientContactPhone" ||
            attribute.dataField === "PatientContactTel")
        ) {
          let newAV = JSON.parse(JSON.stringify(attribute.value));
          let phone = newAV.valueContactPoints[0].value;
          let cc = newAV.valueContactPoints[0].countryCode;
          delete newAV.valueContactPoints[0].countryCode;
          newAV.valueContactPoints[0].value = doE164(cc, phone);
          attribute.value = newAV;
        }

        // if (attribute.type === "DATE") {
        //   let av = attribute.value;
        //   if (av && av.valueDate) {
        //     av.valueDate = new Date(av.valueDate).getTime();
        //   }
        // }
      }
      data.challengeFields = clonedChallengeFields;
    }

    let api = API_ENDPOINTS.acceptInviteV1;

    if (isVerification) {
      data.email = verificationData.email;
      data.phoneNumber = verificationData.phoneNumber;
      data.patientId = verificationData.patientId;
      api = api + "?verifyContactDetails=true";
    }

    data.locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);

    return http.request(api, "post", data);
  }

  onboardingSetCredentials(
    inviteCode,
    loginUserId,
    password,
    acceptedTermsAndCondition,
    onboardingType,
    patientId,
    attributes,
  ) {
    let data = {
      loginUserId,
      password,
      acceptedTermsAndCondition,
    };

    if (onboardingType === "signup") {
      data.patientId = patientId;
    } else {
      data.inviteCode = inviteCode;
    }

    if (attributes && attributes.length >= 1) {
      data.patientAttributes = attributes;
    }
    data.locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);

    return http.request(API_ENDPOINTS.setLoginCredentialsV1, "post", data);
  }

  getPatientViewTabs() {
    const intlProvider = I18nProvider.getIntlProvider();
    let facilityName = store.getState().user.data.facilityName;
    let userPrivileges =
      store.getState().user && store.getState().user.data
        ? store.getState().user.data.userPrivileges || []
        : [];

    // Step 1: Build all possible tabs with current locale labels
    let availableTabs = this.buildAllPossibleTabs(
      intlProvider,
      facilityName,
      userPrivileges,
    );

    // Step 2: Get saved custom order (just tab IDs)
    let savedOrder = patientViewTabsStorageService.getTabOrderForCurrentStaff();

    // Step 3: Apply custom ordering while preserving filtered set
    return this.applySavedOrder(availableTabs, savedOrder);
  }

  buildAllPossibleTabs(intlProvider, facilityName, userPrivileges) {
    let tabs = [];
    let conso = FeaturesService.isConsolidatedReportEnabled();

    // Always start with statuses
    if (conso && userPrivileges.indexOf("CONSOLIDATED_REPORT_FIRST") !== -1) {
      tabs.push({
        id: "summary",
        label: intlProvider.formatMessage(Messages.summary_uc),
      });
      tabs.push({
        id: "statuses",
        label: intlProvider.formatMessage(Messages.statuses_uc),
      });
    } else {
      tabs.push({
        id: "statuses",
        label: intlProvider.formatMessage(Messages.statuses_uc),
      });
    }

    // Staff notes and patient interactions
    if (
      !FeaturesService.shouldHideStaffNotes() &&
      userPrivileges.indexOf("HIDE_STAFF_NOTES") === -1
    ) {
      tabs.push({
        id: "staff-notes",
        label: intlProvider.formatMessage(Messages.staff_notes_uc),
      });
      if (userPrivileges.indexOf("HIDE_PATIENT_COMMS") === -1) {
        tabs.push({
          id: "patient-interactions",
          label: intlProvider.formatMessage(Messages.patient_interactions_uc),
        });
      }
    }

    // Care plans / Actions
    if (userPrivileges.indexOf("HIDE_PATIENT_CARE_PLAN") === -1) {
      if (facilityName === "POD" || facilityName === "WEYAK") {
        tabs.push({
          id: "care-plans",
          label: intlProvider.formatMessage(Messages.actions_uc),
        });
      } else {
        tabs.push({
          id: "care-plans",
          label: intlProvider.formatMessage(Messages.care_plan_uc),
        });
      }
    }

    // Assessments
    if (userPrivileges.indexOf("HIDE_ASSESSMENTS_MENU") === -1) {
      tabs.push({
        id: "assessments",
        label: intlProvider.formatMessage(Messages.assessments_uc),
      });
    }

    // Summary (if not already added)
    if (conso && userPrivileges.indexOf("CONSOLIDATED_REPORT_FIRST") === -1) {
      tabs.push({
        id: "summary",
        label: intlProvider.formatMessage(Messages.summary_uc),
      });
    }

    // Tasks
    if (
      FeaturesService.canTasks() &&
      userPrivileges.indexOf("HIDE_TASK_MENU") === -1
    ) {
      tabs.push({
        id: "tasks",
        label: intlProvider.formatMessage(Messages.tasks_uc),
      });
    }

    // Episodes of care
    if (
      FeaturesService.showEpisodesOfCare() &&
      userPrivileges.indexOf("SHOW_EPISODES_OF_CARE") === -1
    ) {
      tabs.push({
        id: "episodes-of-care",
        label: intlProvider.formatMessage(Messages.episodes_of_care),
      });
    }

    // Appointments
    if (
      FeaturesService.canDoAppointments() &&
      userPrivileges.indexOf("HIDE_APPOINTMENT_MENU") === -1
    ) {
      tabs.push({
        id: "appointments",
        label: intlProvider.formatMessage(Messages.appointments_uc),
      });
    }

    // AI Summary
    if (
      FeaturesService.isAiEnabled() &&
      FeaturesService.isPatientSummaryEnabled()
    ) {
      tabs.push({
        id: "ai-summary",
        label: intlProvider.formatMessage(Messages.ai_summary_uc),
      });
    }

    // Usage (always present)
    tabs.push({
      id: "usage",
      label: intlProvider.formatMessage(Messages.usage_uc),
    });

    // Audit logs
    if (userPrivileges.indexOf("SHOW_AUDIT_LOGS") !== -1) {
      tabs.push({
        id: "audit-logs",
        label: intlProvider.formatMessage(Messages.audit_logs_uc),
      });
    }

    // Medications
    if (FeaturesService.canDoMedications()) {
      tabs.push({
        id: "medications",
        label: intlProvider.formatMessage(Messages.medications_uc),
      });
    }

    // Allergies
    if (FeaturesService.canDoAllergies()) {
      tabs.push({
        id: "allergies",
        label: intlProvider.formatMessage(Messages.allergies_uc),
      });
    }

    // Diet
    if (FeaturesService.isDietTrackingEnabled()) {
      tabs.push({
        id: "diet",
        label: intlProvider.formatMessage(Messages.diet_uc),
      });
    }

    // Charge items
    if (FeaturesService.enableChargeItems()) {
      tabs.push({
        id: "charge-items",
        label: intlProvider.formatMessage(Messages.charge_items_uc),
      });
    }

    return tabs;
  }

  applySavedOrder(availableTabs, savedOrder) {
    devLog(`🟪 APPLYING saved order:`, savedOrder);
    devLog(
      `🟪 Available tabs:`,
      availableTabs.map((t) => t.id),
    );

    if (!savedOrder || savedOrder.length === 0) {
      devLog(`⚠️ No saved order, returning available tabs as-is`);
      return availableTabs;
    }

    // Create a map of available tabs by ID for quick lookup
    const tabsMap = new Map();
    availableTabs.forEach((tab) => {
      tabsMap.set(tab.id, tab);
    });

    // Apply saved order
    const orderedTabs = [];
    savedOrder.forEach((id) => {
      if (tabsMap.has(id)) {
        orderedTabs.push(tabsMap.get(id));
        tabsMap.delete(id); // Remove from map to track what's been added
      } else {
        devLog(`⚠️ Saved tab ID "${id}" not found in available tabs`);
      }
    });

    // Add any remaining tabs that weren't in the saved order (new tabs)
    tabsMap.forEach((tab) => {
      devLog(`➕ Adding new tab not in saved order: ${tab.id}`);
      orderedTabs.push(tab);
    });

    devLog(
      `✅ Final ordered tabs:`,
      orderedTabs.map((t) => t.id),
    );
    return orderedTabs;
  }

  storePatientViewTabs(tabs) {
    patientViewTabsStorageService.storeTabOrderForCurrentStaff(tabs);
  }

  signupPatient = (data) => {
    data.locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    return http.request(API_ENDPOINTS.patientSignUp, "post", data);
  };

  confirmEmail = (confirmationCode, linkType) => {
    // emailConfirmation: code => `/api/signup/confirm/${code}`,
    let api =
      API_ENDPOINTS.emailConfirmation(confirmationCode) + "?cp=" + linkType;
    return http.request(api, "post");
  };

  validateSignUpField = (data) => {
    data.locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    return http.request(API_ENDPOINTS.signUpValidation, "post", data);
  };

  fetchSignUpProfileFromCode(code) {
    let locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    let api = API_ENDPOINTS.signupProfileFromCode(code);
    if (locale) {
      api += "?locale=" + locale;
    }
    return http.request(api);
  }

  setServerURL(url) {
    http.setBaseURL(url);
    StorageService.addToLocalStorage(API_URL_KEY, url);
  }

  unsubscribeNotifications(region, data) {
    return http.request(API_ENDPOINTS.unsubscribe, "post", data);
  }

  /**
   * Update user's preferred locale
   * @param {string} locale - The locale code to set as preferred
   * @returns {Promise<Object>} API response
   */
  async updatePreferredLocale(locale) {
    try {
      // Update Redux store
      store.dispatch({
        type: UPDATE_PREFERRED_LOCALE,
        data: { preferredLocale: locale },
      });

      // Make API request
      const response = await http.request(
        API_ENDPOINTS.preferredLocale(locale),
        "post",
        {},
      );

      // Clear the profile cache since locale is part of the profile
      this.clearProfileCache();

      return response;
    } catch (error) {
      console.error("Error updating preferred locale:", error);
      throw error;
    }
  }

  createNewVideoCallRoom(patientId, data = {}, type, roomType) {
    return http.request(
      API_ENDPOINTS.videoRoom(patientId, type, roomType),
      "post",
      data
    );
  }

  sendVideoCallStatusMessage = (userRole, patientId, roomName) => {
    let appURL = getAppURL();

    let submitData = {
      title: "Video call",
      type: "STATUS_UPDATE",
      senderCategory: "PATIENT",
      visibility: "ALL",
      detailMessage: `<p class="md-block-unstyled">Staff has initiated a video call with you. Please click <a class="md-inline-link" href="${appURL}/app/video-call/${roomName}" target="_blank" rel="opener">here</a> to join. <a class="md-inline-link" href="${appURL}/video-call/${roomName}" target="_blank" rel="noopener noreferrer">This</a> is for staff.</p>`,
    };

    StatusesService.add(userRole, patientId, submitData);
  };

  joinVideoRoom(roomName) {
    const endpoint = API_ENDPOINTS.joinRoom(roomName);
    const baseURL = http.axios.defaults.baseURL;
    const fullURL = baseURL + endpoint;

    devLog("[VIDEO CALL DEBUG] joinVideoRoom called:", {
      roomName,
      endpoint,
      baseURL,
      fullURL,
      currentDomain: document.domain,
      windowLocation: window.location.href,
    });

    return http.request(endpoint, "post", {});
  }

  joinVideoRoomAuth(roomName) {
    const endpoint = API_ENDPOINTS.joinRoomAuth(roomName);
    const baseURL = http.axios.defaults.baseURL;
    const fullURL = baseURL + endpoint;

    devLog("[VIDEO CALL DEBUG] joinVideoRoomAuth called:", {
      roomName,
      endpoint,
      baseURL,
      fullURL,
      currentDomain: document.domain,
      windowLocation: window.location.href,
    });

    return http.request(endpoint, "post", {});
  }

  unjoinVideoRoomAuth(roomName) {
    return http.request(API_ENDPOINTS.unjoinRoomAuth(roomName), "post", {});
  }

  saveVideoCallTranscript(patientId, roomName, transcript) {
    return http.request(
      API_ENDPOINTS.videoRoomTranscript(patientId, roomName),
      "post",
      { transcript }
    );
  }

  fetchVideoRoom(roomName) {
    return http.request(API_ENDPOINTS.videoRoomDetails(roomName));
  }

  /**
   * Get care teams with caching, delegating to staffService
   * @param {string} [forOrg] - Optional organization ID to filter care teams
   * @returns {Promise<Array>} List of care teams
   */
  async getCareTeams(forOrg) {
    // Use staffService's cached implementation to avoid duplicate code
    return staffService.getCareTeams(forOrg);
  }

  getChannelTabs() {
    const intlProvider = I18nProvider.getIntlProvider();
    let tabs = [
      {
        id: "statuses",
        label: intlProvider.formatMessage(Messages.statuses_uc),
      },
    ];

    return tabs;
  }

  completePreValidationForInviteFlow(onboardingType, inviteCode) {
    if (onboardingType !== "invite" || !inviteCode) {
      return;
    }

    let locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    return http.request(API_ENDPOINTS.inviteOnboardingEvents(), "post", {
      inviteCode,
      userAction: "PRE_VALIDATION",
      locale,
    });
  }

  completeSignupIntroForInviteFlow(onboardingType, inviteCode) {
    if (onboardingType !== "invite" || !inviteCode) {
      return;
    }
    let locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    return http.request(API_ENDPOINTS.inviteOnboardingEvents(), "post", {
      inviteCode,
      userAction: "INTRO",
      locale,
    });
  }

  completeSignupConsentForInviteFlow(onboardingType, inviteCode) {
    if (onboardingType !== "invite" || !inviteCode) {
      return;
    }
    let locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    return http.request(API_ENDPOINTS.inviteOnboardingEvents(), "post", {
      inviteCode,
      userAction: "CONSENT",
      locale,
    });
  }

  // acceptInviteForInviteFlow(inviteCode) {
  //   if (!inviteCode) {
  //     return;
  //   }
  //   return http.request(API_ENDPOINTS.inviteOnboardingEvents(), "post", {
  //     inviteCode,
  //     userAction: "ACCEPT_INVITE"
  //   });
  // }

  dataEnteredByPatientForInviteFlow(inviteCode) {
    if (!inviteCode) {
      return;
    }
    let locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    return http.request(API_ENDPOINTS.inviteOnboardingEvents(), "post", {
      inviteCode,
      userAction: "DATA_SUBMITTED",
      locale,
    });
  }

  doLogout() {
    return http.request(API_ENDPOINTS.logout(), "post", {});
  }

  optInForMfa(mechanism) {
    return http.request(API_ENDPOINTS.mfaStatus(), "post", {
      mfa_mechanism: mechanism,
    });
  }

  optOutForMfa() {
    return http.request(API_ENDPOINTS.mfaStatus(), "post", {
      mfa_mechanism: "OPTED_OUT_2FA",
    });
  }

  confirmOptOutForMfa(token) {
    return http.request(API_ENDPOINTS.mfaStatus(), "post", {
      mfa_mechanism: "OPTED_OUT_2FA",
      token: token,
    });
  }

  requestPatientMfaOptOut() {
    return http.request(API_ENDPOINTS.disableTwoFA(), "post", {
      mfa_mechanism: "OPTED_OUT_2FA",
    });
  }

  confirmPatientMfaOptOut(token) {
    return http.request(API_ENDPOINTS.disableTwoFA(), "post", {
      mfa_mechanism: "OPTED_OUT_2FA",
      token: token,
    });
  }

  // setupCredentialsForInviteFlow(inviteCode) {
  //   if (!inviteCode) {
  //     return;
  //   }
  //   return http.request(API_ENDPOINTS.inviteOnboardingEvents(), "post", {
  //     inviteCode,
  //     userAction: "SET_UP_CREDENTIALS"
  //   });
  // }

  identifySelfSignupPatient(facilityCode, challengeFields) {
    let locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    return http.request(API_ENDPOINTS.identifySelfSignupPatient(), "post", {
      facilityCode,
      challengeFields,
      locale,
    });
  }

  onboardingSetCredentialsV2(patientId, loginUserId, password) {
    let data = {
      patientId: patientId,
      loginUserId,
      password,
      acceptedTermsAndCondition: true,
      locale: StorageService.getFromSessionStorage(ONBOARDING_LOCALE),
    };
    return http.request(API_ENDPOINTS.setLoginCredentialsV2, "post", data);
  }

  addUserEvent(event) {
    let role = store.getState().user.role;
    if (!isPatientOrMember(role)) {
      return;
    }

    return http.request(API_ENDPOINTS.userEvents(), "post", event);
  }

  addOnboardingEvent(inviteCode, event) {
    let locale = StorageService.getFromSessionStorage(ONBOARDING_LOCALE);
    return http.request(API_ENDPOINTS.inviteOnboardingEvents(), "post", {
      inviteCode,
      value: event,
      locale,
    });
  }
  invalidateSessions(field, value) {
    return http.request(API_ENDPOINTS.forceRelogin(), "post", {
      [field]: value,
    });
  }

  getAccessToken(type, roomName, auth) {
    if (!!auth) {
      return http.request(API_ENDPOINTS.accessTokensAuth(type));
    }
    return http.request(API_ENDPOINTS.accessTokens(type, roomName));
  }

  revokeWebexAccessToken() {
    return http.request(API_ENDPOINTS.revokeWebexAccess(), "DELETE");
  }

  fetchAccessToken(type, code) {
    return http.request(API_ENDPOINTS.accessTokenFromCode(type, code));
  }

  addParticipantsToVideoCall(data) {
    return http.request(API_ENDPOINTS.addVideoParticipants(), "post", data);
  }

  endVideoCall(data) {
    return http.request(API_ENDPOINTS.endVideoCall(), "post", data);
  }
}

const userService = new UserService();
export default userService;
